<template>
  <div class="google-signin-container">
    <!-- Google Sign-In Button -->
    <button
      @click="handleGoogleSignIn"
      :disabled="isLoading"
      class="btn btn-google"
      :class="{ 'loading': isLoading }"
    >
      <div class="btn-content">
        <div class="google-icon" v-if="!isLoading">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="20" height="20">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
        </div>
        <div class="spinner" v-if="isLoading">
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        <span class="btn-text">
          {{ isLoading ? 'Signing in...' : buttonText }}
        </span>
      </div>
    </button>

    <!-- Email Verification Modal -->
    <div v-if="showVerificationModal" class="modal fade show" style="display: block;" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-envelope-check text-primary"></i>
              Email Verification Required
            </h5>
            <button type="button" class="btn-close" @click="closeVerificationModal"></button>
          </div>
          <div class="modal-body">
            <div class="text-center mb-4">
              <div class="verification-icon">
                <i class="bi bi-envelope-check" style="font-size: 3rem; color: #3b82f6;"></i>
              </div>
              <h6 class="mt-3">Check Your Email</h6>
              <p class="text-muted">
                We've sent a verification link to <strong>{{ userEmail }}</strong>. 
                Please check your email and click the link to verify your account.
              </p>
            </div>
            
            <div class="verification-steps">
              <div class="step">
                <i class="bi bi-1-circle text-primary"></i>
                <span>Check your email inbox</span>
              </div>
              <div class="step">
                <i class="bi bi-2-circle text-primary"></i>
                <span>Click the verification link</span>
              </div>
              <div class="step">
                <i class="bi bi-3-circle text-primary"></i>
                <span>Return to sign in</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-outline-secondary" 
              @click="resendVerificationEmail"
              :disabled="resendCooldown > 0"
            >
              <i class="bi bi-arrow-clockwise"></i>
              {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Email' }}
            </button>
            <button type="button" class="btn btn-primary" @click="closeVerificationModal">
              <i class="bi bi-check-lg"></i>
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal backdrop -->
    <div v-if="showVerificationModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import toast from '@/services/toastService';
import googleAuthService from '@/services/googleAuthService';

export default {
  name: 'GoogleSignIn',
  props: {
    role: {
      type: String,
      required: true,
      validator: (value) => ['student', 'teacher', 'parent'].includes(value)
    },
    buttonText: {
      type: String,
      default: 'Continue with Google'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['success', 'error', 'loading'],
  setup(props, { emit }) {
    const isLoading = ref(false);
    const showVerificationModal = ref(false);
    const userEmail = ref('');
    const resendCooldown = ref(0);
    const resendTimer = ref(null);

    // Initialize Google OAuth on mount
    onMounted(async () => {
      try {
        // Use Google Client ID from environment variable
        const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;
        
        if (!clientId) {
          throw new Error('VUE_APP_GOOGLE_CLIENT_ID is not set. Google Sign-In will not initialize.');
        }
        
        await googleAuthService.initialize(clientId);
      } catch (error) {
        console.error('Failed to initialize Google OAuth:', error);
        toast.error('Failed to initialize Google Sign-In');
      }
    });

    // Handle Google Sign-In
    const handleGoogleSignIn = async () => {
      if (isLoading.value || props.disabled) return;

      try {
        isLoading.value = true;
        emit('loading', true);

        const result = await googleAuthService.signInWithPopup(props.role);

        if (result.success) {
          if (result.is_new_user) {
            if (result.auto_verified) {
              // New user was auto-verified (email service not configured)
              toast.success(result.message || 'Account created and verified! Welcome to LifeSkill!');
              emit('success', result);
            } else if (result.email_sent) {
              // New user needs email verification
              userEmail.value = result.user.email;
              showVerificationModal.value = true;
              
              toast.success('Account created! Please verify your email to continue.');
            } else {
              // Email sending failed, show verification modal with resend option
              userEmail.value = result.user.email;
              showVerificationModal.value = true;
              
              toast.warning('Account created but verification email could not be sent. You can try resending it.');
            }
          } else {
            // Existing user, successful login
            toast.success('Successfully signed in with Google!');
            emit('success', result);
          }
        } else {
          throw new Error(result.message || 'Sign-in failed');
        }
      } catch (error) {
        console.error('Google sign-in error:', error);
        
        if (error.message.includes('verify your email')) {
          // Show verification modal if email verification is required
          showVerificationModal.value = true;
        } else {
          toast.error(error.message || 'Failed to sign in with Google');
          emit('error', error);
        }
      } finally {
        isLoading.value = false;
        emit('loading', false);
      }
    };

    // Close verification modal
    const closeVerificationModal = () => {
      showVerificationModal.value = false;
      userEmail.value = '';
    };

    // Resend verification email
    const resendVerificationEmail = async () => {
      if (resendCooldown.value > 0) return;

      try {
        await googleAuthService.resendVerificationEmail(userEmail.value, props.role);
        toast.success('Verification email sent successfully!');
        
        // Start cooldown timer
        resendCooldown.value = 60;
        resendTimer.value = setInterval(() => {
          resendCooldown.value--;
          if (resendCooldown.value <= 0) {
            clearInterval(resendTimer.value);
            resendTimer.value = null;
          }
        }, 1000);
      } catch (error) {
        console.error('Resend verification error:', error);
        toast.error(error.message || 'Failed to resend verification email');
      }
    };

    // Cleanup timer on unmount
    const cleanup = () => {
      if (resendTimer.value) {
        clearInterval(resendTimer.value);
        resendTimer.value = null;
      }
    };

    return {
      isLoading,
      showVerificationModal,
      userEmail,
      resendCooldown,
      handleGoogleSignIn,
      closeVerificationModal,
      resendVerificationEmail,
      cleanup
    };
  },
  beforeUnmount() {
    this.cleanup();
  }
};
</script>

<style scoped>
.google-signin-container {
  width: 100%;
}

.btn-google {
  width: 100%;
  height: 48px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 1rem;
  color: #374151;
  position: relative;
  overflow: hidden;
}

.btn-google:hover:not(:disabled) {
  border-color: #d1d5db;
  background: #f9fafb;
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.btn-google:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-google.loading {
  pointer-events: none;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.google-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-text {
  font-weight: 600;
  color: #374151;
}

/* Modal Styles */
.modal {
  z-index: 1055;
}

.modal-backdrop {
  z-index: 1050;
}

.modal-content {
  border-radius: 15px;
  border: none;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-header {
  border-bottom: 1px solid #e5e7eb;
  padding: 1.5rem;
}

.modal-title {
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-body {
  padding: 2rem 1.5rem;
}

.verification-icon {
  margin-bottom: 1rem;
}

.verification-steps {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1.5rem;
}

.step {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: #4b5563;
}

.step:last-child {
  margin-bottom: 0;
}

.step i {
  font-size: 1.1rem;
  width: 20px;
  text-align: center;
}

.modal-footer {
  border-top: 1px solid #e5e7eb;
  padding: 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.btn-outline-secondary {
  border-color: #d1d5db;
  color: #6b7280;
}

.btn-outline-secondary:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.btn-primary {
  background: #3b82f6;
  border-color: #3b82f6;
}

.btn-primary:hover {
  background: #2563eb;
  border-color: #2563eb;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-dialog {
    margin: 1rem;
  }
  
  .modal-body {
    padding: 1.5rem 1rem;
  }
  
  .verification-steps {
    padding: 1rem;
  }
}
</style> 